import React, { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { Shield, Users, UserPlus, Mail, Phone } from 'lucide-react'
import { UserList } from '../components/Admin/UserList'
import { InviteUserForm } from '../components/Admin/InviteUserForm'
import { AdminSettings } from '../components/Admin/AdminSettings'

interface UserData {
  id: string
  email: string
  created_at: string
  last_sign_in_at: string | null
  email_confirmed_at: string | null
  user_type: 'free' | 'invited' | 'paid'
  metadata: {
    created_via?: string
    stripe_customer_id?: string
    lifetime_access: boolean
  }
  subscription: any
}

interface Stats {
  total_users: number
  free_users: number
  invited_users: number
  paid_users: number
}

export const AdminDashboard: React.FC = () => {
  const { user, session, loading: authLoading } = useAuth()
  const navigate = useNavigate()
  const [isAdmin, setIsAdmin] = React.useState(false)
  const [checking, setChecking] = React.useState(true)
  const [loading, setLoading] = React.useState(false)
  const [users, setUsers] = React.useState<UserData[]>([])
  const [stats, setStats] = React.useState<Stats>({
    total_users: 0,
    free_users: 0,
    invited_users: 0,
    paid_users: 0,
  })
  const [error, setError] = React.useState<string | null>(null)
  const [showInviteForm, setShowInviteForm] = React.useState(false)
  const [activeTab, setActiveTab] = React.useState<'users' | 'sms'>('users')

  // Fetch users from API
  const fetchUsers = async () => {
    if (!session?.access_token) {
      setError('No access token available')
      return
    }

    setLoading(true)
    setError(null)

    try {
      const baseUrl = import.meta.env.VITE_API_BASE_URL || window.location.origin
      const response = await fetch(`${baseUrl}/api/admin/list-users`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
        },
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to fetch users')
      }

      const data = await response.json()
      setUsers(data.users)
      setStats(data.stats)
    } catch (err) {
      console.error('Error fetching users:', err)
      setError(err instanceof Error ? err.message : 'Failed to fetch users')
    } finally {
      setLoading(false)
    }
  }

  // Delete user handler
  const handleDeleteUser = async (userId: string) => {
    if (!session?.access_token) {
      setError('No access token available')
      return
    }

    // Find user for confirmation
    const userToDelete = users.find(u => u.id === userId)
    if (!userToDelete) return

    // Build confirmation message
    let confirmMessage = `Are you sure you want to delete ${userToDelete.email}?`

    // Add subscription cancellation warning if user has active subscription
    if (userToDelete.user_type === 'paid' && userToDelete.subscription?.status === 'active') {
      confirmMessage += `\n\nThis will also IMMEDIATELY CANCEL their active ${userToDelete.subscription.plan} subscription.`
    }

    confirmMessage += '\n\nThis action cannot be undone.'

    // Confirm deletion
    if (!window.confirm(confirmMessage)) {
      return
    }

    try {
      const baseUrl = import.meta.env.VITE_API_BASE_URL || window.location.origin
      const response = await fetch(`${baseUrl}/api/admin/delete-user`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ userId }),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Failed to delete user')
      }

      // Refresh user list
      await fetchUsers()
    } catch (err) {
      console.error('Error deleting user:', err)
      setError(err instanceof Error ? err.message : 'Failed to delete user')
    }
  }

  // Reset password handler
  const handleResetPassword = async (userEmail: string) => {
    if (!session?.access_token) {
      setError('No access token available')
      return
    }

    // Confirm action
    if (!window.confirm(`Send password reset email to ${userEmail}?`)) {
      return
    }

    try {
      const baseUrl = import.meta.env.VITE_API_BASE_URL || window.location.origin
      const response = await fetch(`${baseUrl}/api/admin/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ email: userEmail }),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Failed to send password reset')
      }

      alert(`Password reset email sent to ${userEmail}`)
    } catch (err) {
      console.error('Error resetting password:', err)
      setError(err instanceof Error ? err.message : 'Failed to send password reset')
    }
  }

  // Grant lifetime access handler
  const handleGrantLifetimeAccess = async (userId: string) => {
    if (!session?.access_token) {
      setError('No access token available')
      return
    }

    // Find user for confirmation
    const userToGrant = users.find(u => u.id === userId)
    if (!userToGrant) return

    // Confirm action
    if (!window.confirm(`Grant lifetime free access to ${userToGrant.email}?`)) {
      return
    }

    try {
      const baseUrl = import.meta.env.VITE_API_BASE_URL || window.location.origin
      const response = await fetch(`${baseUrl}/api/admin/grant-lifetime-access`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ userId }),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Failed to grant lifetime access')
      }

      // Refresh user list
      await fetchUsers()
      alert(`Lifetime access granted to ${userToGrant.email}`)
    } catch (err) {
      console.error('Error granting lifetime access:', err)
      setError(err instanceof Error ? err.message : 'Failed to grant lifetime access')
    }
  }

  useEffect(() => {
    const checkAdminAccess = async () => {
      // Wait for auth to finish loading
      if (authLoading) {
        return
      }

      if (!user) {
        navigate('/')
        return
      }

      // Check if user is admin
      const adminEmail = 'georgemfarnell@gmail.com'
      if (user.email !== adminEmail) {
        navigate('/')
        return
      }

      setIsAdmin(true)
      setChecking(false)

      // Fetch users after confirming admin access
      await fetchUsers()
    }

    checkAdminAccess()
  }, [user, authLoading, navigate, session])

  if (checking) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-dark-primary via-dark-secondary to-dark-tertiary flex items-center justify-center">
        <div className="text-light-primary">Verifying admin access...</div>
      </div>
    )
  }

  if (!isAdmin) {
    return null
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-dark-primary via-dark-secondary to-dark-tertiary">
      {/* Header */}
      <header className="bg-dark-secondary/50 backdrop-blur-sm border-b border-dark-tertiary/30">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-gradient-to-br from-primary-600 to-secondary-600 rounded-xl flex items-center justify-center">
                <Shield className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-light-primary">Admin Dashboard</h1>
                <p className="text-sm text-light-tertiary">Manage users and system settings</p>
              </div>
            </div>
            <button
              onClick={() => navigate('/chat')}
              className="px-4 py-2 bg-dark-tertiary hover:bg-dark-quaternary text-light-primary rounded-lg transition-colors"
            >
              Back to Chat
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-dark-secondary/50 backdrop-blur-sm border border-primary-600/20 rounded-2xl p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-light-tertiary text-sm">Total Users</p>
                <p className="text-3xl font-bold text-light-primary mt-1">
                  {loading ? '—' : stats.total_users}
                </p>
              </div>
              <Users className="w-12 h-12 text-primary-400" />
            </div>
          </div>

          <div className="bg-dark-secondary/50 backdrop-blur-sm border border-primary-600/20 rounded-2xl p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-light-tertiary text-sm">Invited Users</p>
                <p className="text-3xl font-bold text-light-primary mt-1">
                  {loading ? '—' : stats.invited_users}
                </p>
              </div>
              <Mail className="w-12 h-12 text-purple-400" />
            </div>
          </div>

          <div className="bg-dark-secondary/50 backdrop-blur-sm border border-primary-600/20 rounded-2xl p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-light-tertiary text-sm">Paid Users</p>
                <p className="text-3xl font-bold text-light-primary mt-1">
                  {loading ? '—' : stats.paid_users}
                </p>
              </div>
              <UserPlus className="w-12 h-12 text-green-400" />
            </div>
          </div>

          <div className="bg-dark-secondary/50 backdrop-blur-sm border border-primary-600/20 rounded-2xl p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-light-tertiary text-sm">Free Users</p>
                <p className="text-3xl font-bold text-light-primary mt-1">
                  {loading ? '—' : stats.free_users}
                </p>
              </div>
              <Users className="w-12 h-12 text-gray-400" />
            </div>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-6 p-4 bg-red-900/30 border border-red-500/50 rounded-xl backdrop-blur-sm">
            <div className="flex items-center space-x-3">
              <div className="w-5 h-5 rounded-full bg-red-500 flex items-center justify-center">
                <span className="text-white text-xs">!</span>
              </div>
              <div>
                <p className="text-red-300 text-sm font-medium">Error Loading Users</p>
                <p className="text-red-400 text-xs">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab('users')}
            className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all ${
              activeTab === 'users'
                ? 'bg-gradient-to-r from-primary-600 to-secondary-600 text-white'
                : 'bg-dark-secondary/50 text-light-tertiary hover:bg-dark-tertiary'
            }`}
          >
            <Users className="w-5 h-5" />
            User Management
          </button>
          <button
            onClick={() => setActiveTab('sms')}
            className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all ${
              activeTab === 'sms'
                ? 'bg-gradient-to-r from-primary-600 to-secondary-600 text-white'
                : 'bg-dark-secondary/50 text-light-tertiary hover:bg-dark-tertiary'
            }`}
          >
            <Phone className="w-5 h-5" />
            SMS Notifications
          </button>
        </div>

        {/* User Management Section */}
        {activeTab === 'users' && (
          <div className="bg-dark-secondary/50 backdrop-blur-sm border border-primary-600/20 rounded-2xl p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-light-primary">User Management</h2>
              <button
                onClick={() => setShowInviteForm(true)}
                className="px-4 py-2 bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-700 hover:to-secondary-700 text-white rounded-lg font-medium transition-all"
              >
                <UserPlus className="w-5 h-5 inline mr-2" />
                Invite User
              </button>
            </div>

            {loading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
                <p className="text-light-tertiary mt-4">Loading users...</p>
              </div>
            ) : (
              <UserList
                users={users}
                onDeleteUser={handleDeleteUser}
                onResetPassword={handleResetPassword}
                onGrantLifetimeAccess={handleGrantLifetimeAccess}
              />
            )}
          </div>
        )}

        {/* SMS Notifications Section */}
        {activeTab === 'sms' && (
          <div className="bg-dark-secondary/50 backdrop-blur-sm border border-primary-600/20 rounded-2xl p-6">
            <AdminSettings />
          </div>
        )}
      </main>

      {/* Invite User Modal */}
      {showInviteForm && session?.access_token && (
        <InviteUserForm
          onClose={() => setShowInviteForm(false)}
          onSuccess={() => {
            fetchUsers() // Refresh user list
          }}
          accessToken={session.access_token}
        />
      )}
    </div>
  )
}
